import template from 'lodash-es/template';
import Modal, { SIZE as MODAL_SIZE } from '@shared/ui-modal';
import { T_ } from '@shared/translation';
import { getCSRFToken } from '@shared/cookie';
import { isAuth, currentRegion, currentDomain, currentLanguage } from 'server-data/footer-locale-selector';
import bodyTemplate from './template-body.html';
import headerTemplate from './template-header.html';
import footerTemplate from './template-footer.html';
import 'css/flux/layout/LocaleSelector.less';

/**
 * Change the language of the site by submitting the form to the PHP endpoint
 * that changes the language, then brings the user back to the previous page
 * they are in.
 * @param  {Node} form The form element.
 */
function changeLanguage(formEl) {
  const searchParam = window.location.search
    .toString()
    .substr(1)
    .split('&')
    .filter(/./.test.bind(/^((?!t=\w).)*$/))
    .join('&');
  const filteredSearchString = searchParam ? `?${searchParam}` : '';

  // Populate form data with CSRF Token & stripped redirect URL then submit it.
  formEl.csrf_token.value = getCSRFToken();
  formEl.last_page.value = window.location.pathname +
    filteredSearchString + window.location.hash;
  formEl.submit();
}

/**
 * Creates the popover and initializes the initial value on the inputs and
 * links.
 * @param  {Array} domains   The domains of Freelancer sent by the AJAX
 *                           endpoint called from loadLanguageSelectorData.
 * @param  {Array} languages The languages of Freelancer sent by the AJAX
 *                           endpoint called from loadLanguageSelectorData.
 */
function render(domains, languages) {
  // Initialize the popover
  const modalTemplates = {
    header: template(headerTemplate)({
      T_,
    }),
    body: template(bodyTemplate)({
      domains,
      T_,
      currentRegion,
      currentDomain,
    }),
    // Only show the language selector if user is authenticated
    footer: !isAuth ? '' : template(footerTemplate)({
      T_,
      languages,
      currentLanguage,
    }),
  };

  const modal = new Modal('locale-selector-modal', modalTemplates, {
    size: MODAL_SIZE.LARGE,
    extraClass: 'LocaleSelector',
  });

  const languageSelectorEl = document.getElementById('language-selector');
  const languageSelectorFormEl = document
    .getElementById('language-selector-form');
  const buttonEl = document.getElementById('locale-selector');

  // Set the events necessary
  // Event to trigger language switch based on the dropdown
  if (languageSelectorEl) {
    languageSelectorEl.addEventListener('change', () =>
      changeLanguage(languageSelectorFormEl));
  }

  // For a11y, set the button to `role="button"` when the JS has kicked in.
  buttonEl.setAttribute('role', 'button');

  // Event to trigger the popover
  buttonEl.addEventListener('click', (event) => {
    // Cancel the default <a> tag behaviour.
    event.preventDefault();
    modal.show();
  });


  // More elegant behaviours when hovering over elements, clicking them and
  // focusing on them for better a11y and mobile behaviours.
  const regionEls = Array.from(document.querySelectorAll('[data-region]'));
  const countryEls = Array.from(document.querySelectorAll('[data-country]'));

  regionEls.forEach((regionEl) => {
    function eventHandler() {
      if (regionEl.classList.contains('is-active')) {
        return;
      }

      regionEls.forEach(el => el.classList.remove('is-active'));
      regionEl.classList.add('is-active');
    }

    // Toggle is-active for clicks and focus
    regionEl.addEventListener('click', eventHandler);
    regionEl.addEventListener('focus', eventHandler, true);
  });

  countryEls.forEach((countryEl) => {
    countryEl.addEventListener('focus', () => {
      if (countryEl.classList.contains('is-active')) {
        return;
      }

      countryEls.forEach(el => el.classList.remove('is-active'));
      countryEl.classList.add('is-active');
    });

    // When hovering over the element take out all is-active on the list and
    // focus on it to use tab immediately.
    countryEl.addEventListener('mouseover', () => {
      countryEls.forEach(el => el.classList.remove('is-active'));
      countryEl.focus();
    });

    countryEl.addEventListener('mouseleave', (event) => {
      event.target.classList.remove('is-active');
      countryEl.blur();
    });
  });
}

// Fetch the information needed to set up the popover.
fetch('/ajax/pinky/footer.php').then((response) => {
  if (response.ok) {
    return response.json();
  }

  const error = new Error(response.statusText);
  error.response = response;
  throw error;
}).then((response) => {
  function onReadyHandler() {
    render(response.data.domains, response.data.languages);
  }

  if (document.readyState !== 'loading') {
    onReadyHandler();
  } else {
    document.addEventListener('DOMContentLoaded', onReadyHandler);
  }
});
